import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CartService } from '../../services/cart.service';
import { Cart, CartItem, SupplierOrder } from '../../models/cart.models';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-cart-dialog',
  template: `
    <div class="cart-dialog">
      <h2 mat-dialog-title>Shopping Cart</h2>

      <mat-dialog-content>
        <ng-container *ngIf="cart$ | async as cart">
          <!-- Empty Cart State -->
          <div
            *ngIf="cart.items.length === 0"
            class="empty-cart"
          >
            <mat-icon>shopping_cart</mat-icon>
            <p>Your cart is empty</p>
          </div>

          <!-- Cart Items -->
          <div
            *ngIf="cart.items.length > 0"
            class="cart-items"
          >
            <!-- Group items by supplier -->
            <div
              *ngFor="let supplier of groupItemsBySupplier(cart.items)"
              class="supplier-group"
            >
              <div class="supplier-header">
                <div class="supplier-info">
                  <h3>{{ supplier.name }}</h3>
                  <span class="supplier-details"
                    >{{ supplier.email }} | {{ supplier.country }}</span
                  >
                </div>
              </div>

              <mat-card
                *ngFor="let item of supplier.items"
                class="item-card"
              >
                <div class="item-details">
                  <div class="item-info">
                    <span class="item-name">{{ item.materialName }}</span>
                    <span class="item-code">{{ item.materialCode }}</span>
                  </div>

                  <div class="item-stock">
                    <span class="stock-label">Available:</span>
                    <span [class]="getStockLevelClass(item.availableStock)">
                      {{ item.availableStock }}
                    </span>
                  </div>

                  <div class="item-actions">
                    <mat-form-field
                      appearance="outline"
                      class="quantity-field"
                    >
                      <mat-label>Qty</mat-label>
                      <input
                        matInput
                        type="number"
                        [min]="item.minimumOrderQuantity"
                        [value]="item.quantity"
                        (change)="updateQuantity(item.id, $event)"
                      />
                      <mat-hint>Min: {{ item.minimumOrderQuantity }}</mat-hint>
                    </mat-form-field>

                    <button
                      mat-icon-button
                      color="warn"
                      (click)="removeItem(item.id)"
                    >
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>
                </div>
              </mat-card>
            </div>

            <!-- Cart Summary -->
            <div class="cart-summary">
              <div class="summary-row">
                <span>Total Items:</span>
                <span>{{ cart.items.length }}</span>
              </div>
              <div class="summary-row">
                <span>Suppliers:</span>
                <span>{{ getSupplierCount(cart.items) }}</span>
              </div>
            </div>
          </div>
        </ng-container>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button
          mat-button
          (click)="close()"
          >Close</button
        >
        <button
          mat-raised-button
          color="warn"
          *ngIf="(cart$ | async)?.items?.length"
          (click)="clearCart()"
        >
          Clear Cart
        </button>
        <button
          mat-raised-button
          color="primary"
          *ngIf="(cart$ | async)?.items?.length"
          (click)="checkout()"
        >
          Create Orders
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .cart-dialog {
        min-width: 600px;
        max-width: 800px;
      }

      .empty-cart {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 48px;
        color: #666;
      }

      .empty-cart mat-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
        margin-bottom: 16px;
      }

      .cart-items {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .supplier-group {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .supplier-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 8px 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      }

      .supplier-info h3 {
        margin: 0;
        font-size: 16px;
        color: #1a237e;
      }

      .supplier-details {
        font-size: 12px;
        color: #666;
      }

      .item-card {
        margin-bottom: 8px;
      }

      .item-details {
        display: grid;
        grid-template-columns: 2fr 1fr 1fr;
        gap: 16px;
        align-items: center;
      }

      .item-info {
        display: flex;
        flex-direction: column;
      }

      .item-name {
        font-weight: 500;
      }

      .item-code {
        font-size: 12px;
        color: #666;
      }

      .item-stock {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .stock-label {
        font-size: 12px;
        color: #666;
      }

      .stock-high {
        color: #2e7d32;
      }

      .stock-medium {
        color: #e65100;
      }

      .stock-low {
        color: #c62828;
      }

      .item-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .quantity-field {
        width: 100px;
      }

      .cart-summary {
        background: #f5f5f5;
        padding: 16px;
        border-radius: 8px;
        margin-top: 16px;
      }

      .summary-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        font-weight: 500;
      }

      @media (max-width: 600px) {
        .cart-dialog {
          min-width: auto;
          width: 100%;
        }

        .item-details {
          grid-template-columns: 1fr;
          gap: 8px;
        }

        .item-actions {
          justify-content: space-between;
        }
      }
    `,
  ],
})
export class CartDialogComponent implements OnInit {
  cart$: Observable<Cart>;

  constructor(
    private dialogRef: MatDialogRef<CartDialogComponent>,
    private cartService: CartService,
    private snackBar: MatSnackBar,
  ) {
    this.cart$ = this.cartService.getCart();
  }

  ngOnInit() {}

  groupItemsBySupplier(items: CartItem[]) {
    return items.reduce((groups, item) => {
      const supplier = groups.find((g) => g.id === item.supplierId);
      if (supplier) {
        supplier.items.push(item);
      } else {
        groups.push({
          id: item.supplierId,
          name: item.supplierName,
          email: item.supplierEmail,
          country: item.supplierCountry,
          items: [item],
        });
      }
      return groups;
    }, [] as any[]);
  }

  getSupplierCount(items: CartItem[]): number {
    return new Set(items.map((item) => item.supplierId)).size;
  }

  getStockLevelClass(stock: number): string {
    if (stock > 50) return 'stock-high';
    if (stock >= 20) return 'stock-medium';
    return 'stock-low';
  }

  updateQuantity(itemId: string, event: Event) {
    const quantity = parseInt((event.target as HTMLInputElement).value, 10);
    if (!isNaN(quantity) && quantity > 0) {
      this.cartService.updateQuantity(itemId, quantity);
    }
  }

  removeItem(itemId: string) {
    this.cartService.removeFromCart(itemId);
  }

  clearCart() {
    this.cartService.clearCart();
  }

  checkout() {
    const orders = this.cartService.createSupplierOrders();
    this.snackBar.open(
      `Created ${orders.length} supplier order${orders.length > 1 ? 's' : ''}`,
      'Close',
      {
        duration: 3000,
      },
    );
    this.dialogRef.close(orders);
  }

  close() {
    this.dialogRef.close();
  }
}
